/* eslint-disable react-hooks/exhaustive-deps */
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';

import { RegisterStage, StatusCode } from '../../../../api/enumerations';
import { totpSetup } from '../../../../api/register';
import appStoreLogo from '../../../../assets/images/appStore.svg';
import GooglePlayLogo from '../../../../assets/images/image 75.svg';
import { ButtonBox } from '../../../../components/UI/Box';
import { IconCheckMS, IconOpenInNewMS } from '../../../../constants/icons';
import useErrorMessage from '../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../hooks/useSnackbar';
import {
  ContainerAppStoreGooglePlay,
  ContainerForBoxApps,
  FilledButton,
  OutlinedStyledButton,
  StyledBox,
  StyledBoxApp,
  StyledTitleBox,
} from '../styles';
import { GoogleAuthStep } from './GoogleAuthStep';

interface MFAProps {
  start: boolean;
  setStart: Dispatch<SetStateAction<boolean>>;
  token: string;
  handleNextStage: (stage: RegisterStage) => void;
}

export function MFA({
  start,
  setStart,
  token,
  handleNextStage,
}: MFAProps): JSX.Element {
  const [next, setNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    secret: '',
    otp_auth_url: '',
  });

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();

  const handleSetStart = (): void => setStart(!start);

  const handleGoToAppStore = (): void => {
    window.open(
      'https://apps.apple.com/us/app/google-authenticator/id388497605',
      '_blank'
    );
  };

  const handleGoToGooglePlay = (): void => {
    window.open(
      // eslint-disable-next-line max-len
      'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=pt_BR&pli=1',
      '_blank'
    );
  };

  const handleSendTOTP = useCallback(async () => {
    setLoading(true);

    try {
      const { data, detail } = await totpSetup({ token });

      if (detail.description) throw new Error(detail.description);

      if (detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente!');

      setData(data);
      setTimeout(() => {
        setNext(true);
      }, 1500);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
    }
  }, [handleSnackbar, token]);

  return (
    <>
      {!start ? (
        <>
          <Typography>
            Proteja sua conta configurando segurança adicional
          </Typography>
          <StyledBox>
            <StyledTitleBox>
              {IconCheckMS} <strong>Segurança Extra</strong>
            </StyledTitleBox>
            <Typography style={{ marginLeft: '2rem' }}>
              Adicione uma camada extra de proteção além da sua senha
            </Typography>
          </StyledBox>
          <StyledBox sx={{ marginBottom: '24px' }}>
            <StyledTitleBox>
              {IconCheckMS} <strong>Fácil de usar</strong>
            </StyledTitleBox>
            <Typography style={{ marginLeft: '2rem' }}>
              Use seu smartphone como segunda forma de autenticação
            </Typography>
          </StyledBox>
        </>
      ) : (
        !next && (
          <ContainerForBoxApps>
            <Typography textAlign="center" fontSize="20px" fontWeight="500">
              Baixe e instale o aplicativo Google Authenticator
            </Typography>
            <Typography textAlign="center" marginBottom="48px">
              O Google Authenticator é 100% gratuito e não requer cadastro
              adicional.
            </Typography>
            <StyledBoxApp>
              <ContainerAppStoreGooglePlay>
                <img src={appStoreLogo} alt="" />
                <Box display="flex" flexDirection="column">
                  <Typography fontWeight="bold">App Store</Typography>
                  <Typography>Para iPhone/iPad</Typography>
                </Box>
              </ContainerAppStoreGooglePlay>
              <IconButton onClick={handleGoToAppStore} color="primary">
                {IconOpenInNewMS}
              </IconButton>
            </StyledBoxApp>
            <StyledBoxApp>
              <ContainerAppStoreGooglePlay>
                <img src={GooglePlayLogo} alt="" />
                <Box display="flex" flexDirection="column">
                  <Typography fontWeight="bold">Google Play</Typography>
                  <Typography>Para Android</Typography>
                </Box>
              </ContainerAppStoreGooglePlay>
              <IconButton onClick={handleGoToGooglePlay} color="primary">
                {IconOpenInNewMS}
              </IconButton>
            </StyledBoxApp>
            <ButtonBox sx={{ gap: '8px', marginTop: '32px' }}>
              <FilledButton onClick={handleSendTOTP}>
                {loading ? (
                  <CircularProgress size={24} sx={{ color: '#fff' }} />
                ) : (
                  'continuar'
                )}
              </FilledButton>
              <OutlinedStyledButton onClick={handleSetStart}>
                voltar
              </OutlinedStyledButton>
            </ButtonBox>
          </ContainerForBoxApps>
        )
      )}
      {next && (
        <GoogleAuthStep
          setNext={setNext}
          data={data}
          handleNextStage={handleNextStage}
          token={token}
        />
      )}
    </>
  );
}
